import { useState, useCallback } from "react";
import { adaptProductToCartItem } from "@icoach/pos/PosScreenPage.util";
import usePosApi from "@apis/usePosApi";

/**
 * POS 系統商品選擇 Hook
 * 整合手動輸入、條碼掃描等商品選擇方式
 * @param {Function} [onProductSelected] - 商品選擇回調（可選，用於受控模式）
 * @param {Object} [value] - 外部傳入的商品值（受控模式）
 * @param {Object} [options] - 配置選項
 * @param {boolean} [options.controlled=false] - 是否為受控模式
 * @returns {Object} 商品選擇相關狀態和方法
 */
const useProductSelection = (onProductSelected, value = null, options = {}) => {
	const { controlled = false } = options;
	const { getPosProductsSkuApi } = usePosApi();

	// 只有在非受控模式下才管理內部狀態
	const [internalSelectedProduct, setInternalSelectedProduct] = useState(null);
	const [isSearching, setIsSearching] = useState(false);
	const [searchKeyword, setSearchKeyword] = useState("");

	// 根據模式決定使用哪個狀態
	const selectedProduct = controlled ? value : internalSelectedProduct;

	/**
	 * 根據 SKU 查找商品
	 * @param {string} sku - 商品 SKU
	 * @returns {Promise<Object|null>} 找到的商品、優惠資訊或 null
	 */
	const findProductBySku = useCallback(
		async sku => {
			if (!sku || !sku.trim()) return null;

			try {
				// 調用 POS 商品 API 搜尋
				const result = await getPosProductsSkuApi(sku.trim());

				if (result?.type === "partnerVoucher") return result;

				if (Array.isArray(result) && result.length > 0) {
					return result[0]; // 返回第一個匹配的商品
				}

				return null;
			} catch (error) {
				// throw error; // 重新拋出錯誤，讓上層處理
			}
		},
		// eslint-disable-next-line
		[getPosProductsSkuApi]
	);

	/**
	 * 統一的商品選擇處理
	 * @param {Object} product - 選中的商品
	 */
	const handleProductSelection = useCallback(
		product => {
			// 只有在非受控模式下才更新內部狀態
			if (!controlled) {
				setInternalSelectedProduct(product);
			}

			// 總是通知外部回調
			if (onProductSelected) {
				onProductSelected(product);
			}
		},
		// eslint-disable-next-line
		[onProductSelected, controlled]
	);

	/**
	 * 處理掃描結果
	 * @param {string} scannedSku - 掃描到的 SKU
	 */
	const handleScanResult = useCallback(
		async scannedSku => {
			setIsSearching(true);

			try {
				// 根據掃描 SKU 查找商品
				const product = await findProductBySku(scannedSku);
				if (product?.type === "partnerVoucher") return product;
				if (product) {
					const adaptedProduct = adaptProductToCartItem(product);
					// 統一通過 handleProductSelection 處理選中邏輯
					handleProductSelection(adaptedProduct);
					setSearchKeyword(adaptedProduct.name || adaptedProduct.sku);
					return true;
				} else {
					// 查詢失敗的提示由 API hook 顯示，不當作掃描成功。
					return false;
				}
			} catch (err) {
				return false;
			} finally {
				setIsSearching(false);
			}
		},
		// eslint-disable-next-line
		[findProductBySku, handleProductSelection]
	);

	/**
	 * 處理手動輸入
	 * @param {string} inputValue - 輸入值
	 */
	const handleManualInput = useCallback(
		inputValue => {
			setSearchKeyword(inputValue);
		},
		// eslint-disable-next-line
		[]
	);

	/**
	 * 清除選擇
	 */
	const clearSelection = useCallback(
		() => {
			// 只有在非受控模式下才清除內部狀態
			if (!controlled) {
				setInternalSelectedProduct(null);
			}
			setSearchKeyword("");

			// 通知外部回調清除選擇
			if (onProductSelected) {
				onProductSelected(null);
			}
		},
		// eslint-disable-next-line
		[onProductSelected, controlled]
	);

	return {
		// 狀態
		selectedProduct,
		isSearching,
		searchKeyword,

		// 方法
		handleProductSelection,
		handleScanResult,
		handleManualInput,
		clearSelection,
		setSearchKeyword,
	};
};

export default useProductSelection;
